import React, { useEffect, useState } from 'react';
import { Rocket, X, Users } from 'lucide-react';

const STORAGE_KEY = 'sprite-vault:early-access-banner-dismissed:v1';

export default function EarlyAccessBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (!dismissed) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="early-access-banner"
      className="relative z-20 max-w-7xl mx-auto px-3 sm:px-6 pt-3 pb-2 sm:pb-3"
    >
      <div
        className="relative rounded-2xl border-[3px] border-[#3a1064] shadow-[0_6px_0_#3a1064] overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at 0% 0%, #ffd900 0%, transparent 45%),' +
            'radial-gradient(circle at 100% 100%, #00b8ff 0%, transparent 45%),' +
            'linear-gradient(135deg, #ff2bd6 0%, #9d3dff 60%, #3a1064 100%)',
        }}
      >
        {/* subtle sparkle pattern */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 2px),' +
              'radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 2px)',
            backgroundSize: '60px 60px, 90px 90px',
            backgroundPosition: '0 0, 30px 30px',
          }}
        />

        <div className="relative p-3 sm:p-4 flex items-center gap-3">
          {/* Rocket sticker */}
          <div className="h-11 w-11 sm:h-14 sm:w-14 rounded-xl sm:rounded-2xl bg-[#ffd900] border-[3px] border-[#1a0440] flex items-center justify-center shadow-[0_4px_0_#1a0440] shrink-0 -rotate-6 animate-[wiggle_2.4s_ease-in-out_infinite]">
            <Rocket className="h-5 w-5 sm:h-7 sm:w-7 text-[#3a1064]" strokeWidth={3} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-yellow-300 font-black text-[10px] sm:text-xs uppercase tracking-[2px] sm:tracking-[3px]">
                Day-One Vault
              </span>
              <span className="text-[10px] sm:text-xs font-black uppercase px-1.5 py-0.5 rounded-full bg-[#ffd900] text-[#3a1064] border-2 border-[#3a1064]">
                Early access
              </span>
            </div>
            <div className="text-white font-black text-sm sm:text-base leading-tight mt-0.5 sm:mt-1">
              Sprite Vault is fresh out of the oven!
              <span className="hidden sm:inline"> 🎉</span>
            </div>
            <div className="text-white/90 font-bold text-[11px] sm:text-xs mt-0.5 sm:mt-1 flex items-center gap-1 flex-wrap">
              <Users className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
              V-Bucks lottery drawings kick off once we hit our{' '}
              <span className="bg-[#3a1064] text-yellow-300 px-1.5 py-0.5 rounded border border-yellow-300 whitespace-nowrap">
                first 100 users
              </span>
              . Invite your squad!
            </div>
            <div className="text-white/85 font-bold text-[11px] sm:text-xs mt-1 sm:mt-1.5 leading-snug">
              As the community grows, the{' '}
              <span className="bg-[#ffd900] text-[#3a1064] px-1.5 py-0.5 rounded border border-[#3a1064] whitespace-nowrap font-black">
                prize pool grows
              </span>{' '}
              and drawings happen{' '}
              <span className="bg-[#00b8ff] text-white px-1.5 py-0.5 rounded border border-[#0070a0] whitespace-nowrap font-black">
                more often
              </span>
              .
            </div>
          </div>

          {/* Dismiss */}
          <button
            data-testid="early-access-dismiss-btn"
            aria-label="Dismiss early-access notice"
            onClick={dismiss}
            className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg sm:rounded-xl bg-white/95 border-2 border-[#3a1064] text-[#3a1064] shadow-[0_2px_0_#3a1064] hover:bg-white active:translate-y-0.5 active:shadow-[0_1px_0_#3a1064] flex items-center justify-center shrink-0"
          >
            <X className="h-4 w-4" strokeWidth={3} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(-6deg); }
          50%      { transform: rotate(6deg); }
        }
      `}</style>
    </div>
  );
}
